import React from 'react';
import {
  Banner,
  CandidateAccolades,
  CandidatePerformanceFeedback,
  Card,
  CardHeader,
  CardSection,
  ContextMenu,
  ContextMenuItem,
  EmptyLayout,
  Form,
  Layout,
  LayoutItem,
  Paragraph,
  SmallHeader,
  Toggle,
  useApi,
  Visible,
  Well,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import { CandidateReputationApi } from '../../api/candidate-reputation-api';
import { EnvUtil } from '../../utils/env-util';
import ProfileCheckmark from '../../public/svg/profile-checkmark.svg';
import { CandidateApi } from '../../api/candidate-api';

import { PerformanceFeedbackFormSchema } from './performance-feedback-form-schema';

export type PerformanceFeedbackProps = {
  candidate: Candidate;
};

export const PerformanceFeedback = ({
  candidate,
}: PerformanceFeedbackProps) => {
  const [{ data: reputationData }] = useApi(
    CandidateReputationApi.readReputationData(candidate.id)
  );
  return (
    <Card name="performanceFeedbackSection">
      <CardHeader>
        <Layout>
          <LayoutItem fluid>
            <SmallHeader>Performance Feedback</SmallHeader>
          </LayoutItem>
          <LayoutItem>
            <ContextMenu direction="left" name="feedbackMenu">
              <ContextMenuItem
                name="requestTspFeedback"
                onClick={() => {
                  window.open(
                    `mailto:${candidate.ownerUser?.email || EnvUtil.axiomAnswersEmail}`
                  );
                }}
              >
                Request feedback review
              </ContextMenuItem>
            </ContextMenu>
          </LayoutItem>
        </Layout>
      </CardHeader>
      <CardSection>
        <Layout direction="vertical" verticalGutter="24px">
          <CandidateAccolades candidateReputation={reputationData} />
          <Well background="gray">
            <Form
              schema={PerformanceFeedbackFormSchema}
              initialValues={{
                showReputation:
                  candidate.showReputation === true ||
                  (candidate.showReputation === null &&
                    reputationData?.hasEnoughRespondents === true),
              }}
              onSubmit={async values => {
                await CandidateApi.updateCandidate(candidate.id, {
                  showReputation: values.showReputation,
                });
                await CandidateApi.refreshCandidate(candidate.id);
              }}
              submitOnChange
            >
              {() => (
                <Toggle
                  name="showReputation"
                  label={
                    <div data-value="PENDO_PERF_FEEDBACK_TOGGLE">
                      Show Performance Feedback to Clients
                    </div>
                  }
                />
              )}
            </Form>
          </Well>
          {candidate.showReputation === false && (
            <Banner impact="high" type="critical" name="optedOutBanner">
              You have opted out of displaying Performance Feedback in your
              profile for clients to review. Clients will be notified that
              you’ve opted out.
            </Banner>
          )}
          {candidate.showReputation === null &&
            !reputationData?.hasEnoughRespondents && (
              <Banner
                impact="high"
                type="critical"
                name="limitedFeedbackBanner"
              >
                Since you have too few reviews, we have opted you out of
                displaying Performance Feedback in your profile for clients to
                review. However, you may choose to opt in at any time.
              </Banner>
            )}
          {reputationData?.displayableSurveyScoreData ? (
            <CandidatePerformanceFeedback
              candidateReputation={reputationData}
            />
          ) : (
            <Well background="light blue" name="limitedFeedbackWell">
              <EmptyLayout
                graphic={
                  <Visible only="largeScreen">
                    <ProfileCheckmark width="98px" height="93px" />
                  </Visible>
                }
              >
                <Layout position="middle left" stretched>
                  <Paragraph>
                    Once you have received enough performance feedback, it will
                    be displayed here for clients to see.
                  </Paragraph>
                </Layout>
              </EmptyLayout>
            </Well>
          )}
        </Layout>
      </CardSection>
    </Card>
  );
};
